import { http, HttpResponse } from 'msw';
import { mockNotarizationFiles, mockNotarizationStatsSummary } from '../fixtures/notarization';
import type { NotarizationFile, NotarizationFileCreateParams } from '@trace/shared/types';

let notarizationsStore = [...mockNotarizationFiles];

export const notarizationHandlers = [
  // 1. Stats Summary
  http.get('/api/system/notarizations/stats', () => {
    const activeCount = notarizationsStore.filter((n) => n.status === 'active').length;
    const dataPreservationCount = notarizationsStore.filter((n) => n.notaryType === 'data_preservation').length;
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        totalCount: notarizationsStore.length,
        activeCount,
        dataPreservationCount,
        monthlyNewCount: notarizationsStore.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // 2. List & Search
  http.get('/api/system/notarizations', ({ request }) => {
    const url = new URL(request.url);
    const keyword = url.searchParams.get('keyword')?.toLowerCase();
    const notaryType = url.searchParams.get('notaryType');
    const status = url.searchParams.get('status');
    const enterpriseId = url.searchParams.get('enterpriseId');

    let list = [...notarizationsStore];

    if (status && status !== 'all') {
      list = list.filter((n) => n.status === status);
    }
    if (notaryType && notaryType !== 'all') {
      list = list.filter((n) => n.notaryType === notaryType);
    }
    if (enterpriseId && enterpriseId !== 'all') {
      list = list.filter((n) => String(n.enterpriseId) === String(enterpriseId));
    }
    if (keyword) {
      list = list.filter(
        (n) =>
          n.notaryNumber.toLowerCase().includes(keyword) ||
          n.notaryOffice.toLowerCase().includes(keyword) ||
          n.notaryTitle.toLowerCase().includes(keyword) ||
          (n.enterpriseName && n.enterpriseName.toLowerCase().includes(keyword)) ||
          n.notaryOfficer.toLowerCase().includes(keyword),
      );
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // 3. Create Notarization
  http.post('/api/system/notarizations', async ({ request }) => {
    const body = (await request.json()) as NotarizationFileCreateParams;
    const newRecord: NotarizationFile = {
      id: String(Date.now()),
      notaryNumber: body.notaryNumber || `(2026)黑哈五证字第${Math.floor(1000 + Math.random() * 9000)}号`,
      notaryOffice: body.notaryOffice || '黑龙江省五常市公证处',
      notaryTitle: body.notaryTitle || '有机稻米全生命周期溯源数据保全公证书',
      notaryType: body.notaryType || 'data_preservation',
      notaryDate: body.notaryDate || new Date().toISOString().slice(0, 10),
      notaryOfficer: body.notaryOfficer || '张建国 公证员',
      fileUrl: body.fileUrl || `ledgers/notarization/${body.notaryNumber}_公证书.pdf`,
      fileName: body.fileName || `${body.notaryTitle}.pdf`,
      fileSize: body.fileSize || 5600000,
      sha256Hash: body.sha256Hash || 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
      status: body.status || 'active',
      enterpriseId: body.enterpriseId,
      enterpriseName: body.enterpriseName || '五常市金穗生态大米加工有限公司',
      remarks: body.remarks || '司法公证保全，文书真实有效',
      createdAt: new Date().toISOString().replace('T', ' ').slice(0, 19),
      updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19),
    };

    notarizationsStore = [newRecord, ...notarizationsStore];

    return HttpResponse.json({
      code: 200,
      message: '公证文书新增成功！',
      data: newRecord,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // 4. Update
  http.put('/api/system/notarizations/:id', async ({ params, request }) => {
    const { id } = params;
    const body = (await request.json()) as Partial<NotarizationFile>;
    const idx = notarizationsStore.findIndex((n) => String(n.id) === String(id));
    if (idx !== -1) {
      notarizationsStore[idx] = {
        ...notarizationsStore[idx],
        ...body,
        updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19),
      } as NotarizationFile;
      return HttpResponse.json({
        code: 200,
        message: '公证文书更新成功！',
        data: notarizationsStore[idx],
        success: true,
        timestamp: new Date().toISOString(),
      });
    }
    return HttpResponse.json({ code: 404, message: '公证文书不存在', success: false }, { status: 404 });
  }),

  // 5. Toggle Status
  http.patch('/api/system/notarizations/:id/status', async ({ params, request }) => {
    const { id } = params;
    const body = (await request.json()) as { status: any };
    const item = notarizationsStore.find((n) => String(n.id) === String(id));
    if (item) {
      item.status = body.status;
      item.updatedAt = new Date().toISOString().replace('T', ' ').slice(0, 19);
      return HttpResponse.json({
        code: 200,
        message: '公证文书状态已更新',
        data: item,
        success: true,
      });
    }
    return HttpResponse.json({ code: 404, message: '公证文书不存在', success: false }, { status: 404 });
  }),

  // 6. Delete
  http.delete('/api/system/notarizations/:id', ({ params }) => {
    const { id } = params;
    notarizationsStore = notarizationsStore.filter((n) => String(n.id) !== String(id));
    return HttpResponse.json({
      code: 200,
      message: '公证文书已删除',
      success: true,
    });
  }),
];
